import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { ProductService } from 'projects/warehousing/src/app/services/product.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Site } from '@shared/models/site';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { tap, filter } from 'rxjs/operators';
import { ShiftContextService } from 'projects/warehousing/src/app/services/shift-context.service';
import { InvCountProductCount } from 'projects/warehousing/src/app/models/inv-count-product-count';
import { InventoryService } from 'projects/warehousing/src/app/services/inventory.service';
import { BayType } from '@shared/models/bay-type';
import { ShiftStatus } from '@shared/models/worked-shift';
import { getSiteInfo } from '@shared/helpers/site-info';
import { once } from '@shared/helpers/once';
import { saveDebounce } from '@shared/helpers/save-debounce';

type TpmForm = FormGroup<{
    id: FormControl<number>;
    productId: FormControl<number>;
    pieces1: FormControl<number>;
    pieces2: FormControl<number>;
    displayOrder: FormControl<number>;
}>;

@Component({
    selector: 'app-tpm-inventory',
    templateUrl: './tpm-inventory.component.html',
    styleUrls: ['./tpm-inventory.component.scss']
})
export class TpmInventoryComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    tpmInventoryForm = new FormGroup({
        productId: new FormControl<number>(null, Validators.required),
        pieces1: new FormControl<number>(0, Validators.required),
        pieces2: new FormControl<number>(0, Validators.required)
    });
    defaultValuefields= { productId: null, pieces1: 0, pieces2: 0, id: 0, displayOrder: 0 };
    products: SelectItem[] = [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    siteId: string;
    site: Site;
    tpmInventoryIsLoaded = false;
    tpmInventoryIsSaved = false;
    siteSubinventoryId = new BehaviorSubject<number>(null);
    isRlc: boolean = false;

    // begin tpm inventory item grid variables
    tpmInventoryItems: TpmForm[];
    autoSaveFormSubscriptions: Subscription[];
    nextIndex: number;

    rowSubscriptions: Subscription[] = [];
    // end tpm inventory item grid variables

    readonly _tpmInventoryItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create TPM Inventory Item',
        detail: 'Fields are required.'
    };

    readonly _tpmInventoryItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'TPM Inventory Item Created',
        detail: ''
    };

    readonly _tpmInventoryItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'TPM Inventory Item Deleted',
        detail: ''
    };
    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit(): void {
        this.tpmInventoryItems = [];

        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.site = context.site;

            this.isRlc = getSiteInfo(context.site).isRlc;

            if (context.status !== ShiftStatus.Open)
                this.tpmInventoryForm.disable();
            else
                this.tpmInventoryForm.enable();
        }));

        const productsObservable = this.productService.ProductsForSubinventory(this.siteSubinventoryId).pipe(tap(products => {
            this.products = products.map(p => ({ value: p.id, label: p.label }));
        }));

        const tpmInventoryObservable = this.inventoryService.CurrentInvProductCount.pipe(
            filter(counts => !!counts),
            tap(counts => {
                this.tpmInventoryItems = counts
                    .filter(x => x.bayType === BayType.TpmProductionLine)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(x => this.addFormGroup(x));
                this.reIndexTpm();

                this.rowSubscriptions.forEach(s => s.unsubscribe());
                this.rowSubscriptions = this.tpmInventoryItems.map(form => form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(_ => this.updateInv(form.controls.id.value)));
            }));

        this.subscriptions = [
            combineLatest([ siteObservable, productsObservable, tpmInventoryObservable ])
                .pipe(this.activityService.Wait('TPM Production'))
                .subscribe()
        ];
    }

    ngOnDestroy(): void {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.rowSubscriptions.forEach(s => s.unsubscribe());
        this.rowSubscriptions = [];
    }

    private updateInv(invId: number): void {
        const form = this.tpmInventoryItems.find(x => x.value.id === invId);
        const inv = {
            id: form.controls.id.value,
            invCountId: this.invCountId,
            productId: form.controls.productId.value,
            pieces1: +form.controls.pieces1.value,
            pieces2: +form.controls.pieces2.value,
            bayType: BayType.TpmProductionLine,
            displayOrder: form.controls.displayOrder.value
        };
        this.inventoryService.updateInvCountProductCount(inv).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Inventory Updated',
                detail: ''
            });
        });
    }

    saveTPMI(): void {
        if (!this.tpmInventoryForm.valid) {
            this.tpmInventoryForm.markAllAsTouched();
            this.messageService.add(this._tpmInventoryItemNotCreatedMsg);
        } else {
            const entry: InvCountProductCount = {
                productId: this.tpmInventoryForm.controls.productId.value,
                pieces1: +this.tpmInventoryForm.controls.pieces1.value,
                pieces2: +this.tpmInventoryForm.controls.pieces2.value,
                id: 0,
                bayType: BayType.TpmProductionLine,
                invCountId: this.invCountId,
                displayOrder: this.nextIndex
            };

            this.inventoryService.createInvCountProductCount(entry).subscribe(_created => {
                const form = this.addFormGroup(_created);
                this.tpmInventoryItems = [ ...this.tpmInventoryItems, form ];

                this.rowSubscriptions.push(form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(_ => this.updateInv(form.controls.id.value)));

                this.tpmInventoryForm.reset(this.defaultValuefields);
                this.reIndexTpm();
                this.messageService.add(this._tpmInventoryItemCreatedMsg);
            });
        }
    }

    addFormGroup(item: InvCountProductCount) {
        return new FormGroup({
            id: new FormControl<number>(item.id),
            productId: new FormControl<number>(item.productId, Validators.required),
            pieces1: new FormControl<number>(item.pieces1, Validators.required),
            pieces2: new FormControl<number>(item.pieces2, Validators.required),
            displayOrder: new FormControl<number>(item.displayOrder)
        });
    }

    deleteTpmInventoryRow(tpmInventoryItem: TpmForm): void {
        this.inventoryService.deleteInvCountProductCount(tpmInventoryItem.controls.id.value).subscribe(() => {
            this.tpmInventoryItems.splice(this.tpmInventoryItems.indexOf(tpmInventoryItem), 1);
            this.messageService.add(this._tpmInventoryItemDeletedMsg);
            this.reIndexTpm();
        });
    }

    reIndexTpm(): void {
        let tpmIndex = 1;
        if (this.tpmInventoryItems !== undefined) {
            for (const tpmInventoryItem of this.tpmInventoryItems.sort((l, r) =>
                    l.controls.displayOrder.value - r.controls.displayOrder.value)) {
                tpmInventoryItem.controls.displayOrder.setValue(tpmIndex);
                tpmIndex++;
            }
        }

        this.nextIndex = tpmIndex++;
    }

    rowTrackBy(_i: number, item: TpmForm): number {
        return item.controls.id.value;
    }
}
